import type { ReactElement, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'

import { cn } from '@/lib/utils'
import { SIDEBAR_WIDTH, useAppContext } from '@/app/shell/app-context'
import { Button } from '@/ui/button'
import { ResizeHandle } from '@/ui/resize-handle'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui/tooltip'

export function ProductSidebar({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  const { collapsed, resizeHandle, toggle } = useProductSidebarControls()

  return (
    <TooltipProvider delay={0}>
      <aside className="bg-sidebar text-sidebar-foreground relative flex h-full min-h-0 min-w-0 flex-col px-2 py-3">
        <div className="hidden @min-[48rem]:contents">
          <ResizeHandle
            label="Resize sidebar"
            orientation="vertical"
            className="-right-1"
            {...resizeHandle}
          />
        </div>
        <nav aria-label={label} className="space-y-5">
          {children}
        </nav>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={cn(
            'text-sidebar-foreground/70 mt-auto hidden shrink-0 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground dark:hover:bg-sidebar-accent @min-[48rem]:inline-flex',
            collapsed ? 'self-center' : 'self-end',
          )}
          onClick={toggle}
        >
          {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
        </Button>
      </aside>
    </TooltipProvider>
  )
}

export function ProductSidebarSection({
  label,
  divided = false,
  children,
}: {
  label: string
  divided?: boolean
  children: ReactNode
}) {
  const { sidebarCollapsed } = useAppContext()

  return (
    <div
      className={cn(
        divided &&
          'border-sidebar-accent border-t pt-5 @min-[48rem]:border-0 @min-[48rem]:pt-0',
        divided &&
          sidebarCollapsed &&
          '@min-[48rem]:border-t @min-[48rem]:pt-5',
      )}
    >
      <div
        className={cn(
          'text-sidebar-foreground/55 sr-only px-2 text-xs font-medium',
          !sidebarCollapsed && '@min-[48rem]:not-sr-only',
        )}
      >
        {label}
      </div>
      <div
        className={cn(
          'space-y-0.5',
          !sidebarCollapsed && '@min-[48rem]:mt-2',
        )}
      >
        {children}
      </div>
    </div>
  )
}

export function ProductSidebarItem({
  label,
  icon: Icon,
  active,
  children,
}: {
  label: string
  icon: LucideIcon
  active: boolean
  children: (props: {
    className: string
    activeOptions: { exact: true }
    'aria-current': 'page' | undefined
    children: ReactNode
  }) => ReactElement
}) {
  const { sidebarCollapsed } = useAppContext()
  const link = children({
    activeOptions: { exact: true },
    'aria-current': active ? 'page' : undefined,
    className: cn(
      'flex h-8 items-center justify-center gap-2 rounded-md px-2 text-sm transition-colors @min-[48rem]:justify-start',
      sidebarCollapsed && '@min-[48rem]:justify-center',
      active
        ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
        : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
    ),
    children: (
      <>
        <Icon aria-hidden="true" className="size-4 shrink-0" />
        <span
          className={cn(
            'sr-only',
            !sidebarCollapsed && '@min-[48rem]:not-sr-only',
          )}
        >
          {label}
        </span>
      </>
    ),
  })

  return (
    <SidebarItemTooltip collapsed={sidebarCollapsed} label={label}>
      {link}
    </SidebarItemTooltip>
  )
}

function SidebarItemTooltip({
  collapsed,
  label,
  children,
}: {
  collapsed: boolean
  label: string
  children: ReactElement
}) {
  return (
    <Tooltip>
      <TooltipTrigger render={children} />
      <TooltipContent
        side="right"
        sideOffset={8}
        className={cn(!collapsed && 'min-[48rem]:hidden')}
      >
        {label}
      </TooltipContent>
    </Tooltip>
  )
}

function useProductSidebarControls() {
  const {
    sidebarCollapsed,
    setSidebarCollapsed,
    sidebarWidth,
    setSidebarWidth,
  } = useAppContext()

  const snap = (collapsed: boolean, width?: number) => {
    document.documentElement.classList.add('product-sidebar-snapping')
    if (width !== undefined) setSidebarWidth(width)
    setSidebarCollapsed(collapsed)
  }

  const resize = (width: number, input: 'pointer' | 'keyboard') => {
    if (input === 'pointer') {
      if (sidebarCollapsed) {
        if (width < SIDEBAR_WIDTH.expandAt) return
        snap(false, Math.max(SIDEBAR_WIDTH.min, width))
        return
      }

      if (width < SIDEBAR_WIDTH.collapseAt) {
        snap(true)
        return
      }
      if (width < SIDEBAR_WIDTH.min) return

      document.documentElement.classList.remove('product-sidebar-snapping')
      setSidebarWidth(width)
      return
    }

    const nextWidth =
      sidebarCollapsed &&
      width > SIDEBAR_WIDTH.collapsed &&
      width < SIDEBAR_WIDTH.min
        ? SIDEBAR_WIDTH.min
        : width

    if (nextWidth < SIDEBAR_WIDTH.min) {
      if (!sidebarCollapsed) snap(true)
      return
    }

    if (sidebarCollapsed) {
      snap(false, nextWidth)
      return
    }

    document.documentElement.classList.remove('product-sidebar-snapping')
    setSidebarWidth(nextWidth)
  }

  return {
    collapsed: sidebarCollapsed,
    resizeHandle: {
      value: sidebarCollapsed ? SIDEBAR_WIDTH.collapsed : sidebarWidth,
      min: SIDEBAR_WIDTH.collapsed,
      max: SIDEBAR_WIDTH.max,
      onChange: resize,
      onDragChange: (dragging: boolean) => {
        if (dragging) {
          document.documentElement.classList.remove(
            'product-sidebar-snapping',
          )
        }
      },
    },
    toggle: () => snap(!sidebarCollapsed),
  }
}
